import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../music24/components/primitives/SMLButton';
import { ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { useIsNativeEmbed } from '../../../music24/navigation/hooks/useIsNativeEmbed';
import { useConfirmWhileBusy } from '../../../common/views/modals/confirmDialog/useConfirmWhileBusy';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { ConnectionDisconnectModal } from '../ConnectionDisconnectModal';
import { EarlyAccessServiceMark } from '../earlyAccess/EarlyAccessServiceMark';
import { catalogServiceName } from '../earlyAccess/earlyAccessCopy';
import { nativeCatalogConnectDeepLink, openNativeLink } from '../nativeConnect';
import { isSignInOnlyCaptureCard, SessionCaptureCardState, sessionCaptureCardState } from './sessionCaptureCardState';
import { SessionCaptureStatusLines } from './SessionCaptureStatusLines';
import { sessionCaptureMessages as messages } from './sessionCaptureMessages';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import catalogStyles from '../Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SessionCaptureConnectionCardProps {
  connection: ServiceConnection;
  /** Opens the phone handoff (web) for a connect or a Reconnect. The app's web view never calls it. */
  onConnect: (integrationType: string) => void;
  onDisconnect: (integrationType: string) => void;
  /**
   * Opens the artist picker: "Choose artist" when the login has no artist chosen, "Change artist"
   * when it has one. Without it the card shows neither button.
   */
  onChooseArtist?: (integrationType: string) => void;
  isBusy: boolean;
  /** Fixed by the screenshot build so two runs draw the same card. */
  now?: number;
  /** Opens a link of the app's own scheme. Tests pass their own function. */
  navigateNative?: (url: string) => void;
}

/**
 * The card of a source whose login happens on its own page inside the Music24 app (Ditto Music).
 * The web cannot read the cookies of another site, so the web button opens the phone handoff.
 * Inside the app's web view the button opens the native connect of the app instead
 * (`<scheme>://catalog/connect/ditto`), where the login runs.
 *
 * A login with no artist chosen shows "Choose artist"; a connected login shows "Change artist".
 * Both open the artist picker (`SessionCaptureArtistModal`), on the web and in the native web view.
 *
 * design: your-intelligence k1 (five states), k2 (the handoff), k6 note ("Choose artist" step)
 */
const SessionCaptureConnectionCardComponent: React.FC<SessionCaptureConnectionCardProps> = ({
  connection,
  onConnect,
  onDisconnect,
  onChooseArtist,
  isBusy,
  now,
  navigateNative = openNativeLink,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const isNativeEmbed = useIsNativeEmbed();
  const { integrationType } = connection;
  const state = sessionCaptureCardState(connection);
  const name = catalogServiceName(intl, integrationType);

  const handle = useMemo(() => {
    if (state === SessionCaptureCardState.connected || state === SessionCaptureCardState.firstRead) {
      return intl.formatMessage(messages.connectedAs, { artist: connection.artist?.name ?? '' });
    }
    if (state === SessionCaptureCardState.reconnect) {
      return intl.formatMessage(messages.signedOut);
    }
    return intl.formatMessage(messages.notConnected);
  }, [connection.artist?.name, intl, state]);

  const connect = useCallback(() => {
    if (isNativeEmbed) {
      navigateNative(nativeCatalogConnectDeepLink(integrationType));
      return;
    }
    onConnect(integrationType);
  }, [integrationType, isNativeEmbed, navigateNative, onConnect]);
  const disconnect = useCallback(() => onDisconnect(integrationType), [integrationType, onDisconnect]);
  const chooseArtist = useCallback(() => onChooseArtist?.(integrationType), [integrationType, onChooseArtist]);
  const openStreaming = useCallback(() => router.push(SMLRoutes.streaming), [router]);
  const disconnectDialog = useConfirmWhileBusy({ isBusy, onConfirm: disconnect });

  const isOn = state === SessionCaptureCardState.connected || state === SessionCaptureCardState.firstRead;
  // A sign-in-only service names the Music24 artist itself and has no number in Streaming yet.
  const signInOnly = isSignInOnlyCaptureCard(connection);
  const canConnect = state === SessionCaptureCardState.notConnected || state === SessionCaptureCardState.reconnect;
  const needsArtist = state === SessionCaptureCardState.needsArtist;
  // The pick works on the web too: the login is stored on the server.
  const canPickArtist = onChooseArtist !== undefined && !signInOnly && (needsArtist || isOn);
  let connectLabel = intl.formatMessage(messages.connectInApp);
  if (state === SessionCaptureCardState.reconnect) {
    connectLabel = intl.formatMessage(messages.reconnect);
  } else if (isNativeEmbed) {
    // Inside the app the button opens the connect itself, so it does not send the artist to the app.
    connectLabel = intl.formatMessage(messages.connect);
  }

  return (
    <div
      className={clsx(catalogStyles.connectionCard, !isOn && catalogStyles.connectionCardOff)}
      data-testid={`session-capture-card-${integrationType}`}
    >
      <EarlyAccessServiceMark integrationType={integrationType} />
      <div className={catalogStyles.connectionBody}>
        <div className={catalogStyles.connectionName}>
          <span
            className={clsx(
              catalogStyles.statusDot,
              state === SessionCaptureCardState.connected && catalogStyles.statusDotOn,
              state === SessionCaptureCardState.firstRead && catalogStyles.statusDotPending
            )}
          />
          {name}
        </div>
        <div className={catalogStyles.connectionHandle}>{handle}</div>
        <SessionCaptureStatusLines connection={connection} state={state} now={now} />
        <div className={catalogStyles.connectionActions}>
          {canConnect && (
            <SMLButton variant="primary" onClick={connect} className={catalogStyles.smallButton} disabled={isBusy}>
              {withBrandWord(connectLabel)}
            </SMLButton>
          )}
          {canPickArtist && needsArtist && (
            <SMLButton variant="primary" onClick={chooseArtist} className={catalogStyles.smallButton} disabled={isBusy}>
              {intl.formatMessage(messages.chooseArtist)}
            </SMLButton>
          )}
          {state === SessionCaptureCardState.connected && !signInOnly && (
            <SMLButton onClick={openStreaming} className={catalogStyles.smallButton}>
              {intl.formatMessage(messages.openStreaming)}
            </SMLButton>
          )}
          {canPickArtist && isOn && (
            <SMLButton onClick={chooseArtist} className={catalogStyles.smallButton} disabled={isBusy}>
              {intl.formatMessage(messages.changeArtist)}
            </SMLButton>
          )}
          {state !== SessionCaptureCardState.notConnected && (
            <SMLButton onClick={disconnectDialog.open} className={catalogStyles.smallButton} disabled={isBusy}>
              {intl.formatMessage({ id: 'app.sml.catalog.connection.disconnect', defaultMessage: 'Disconnect' })}
            </SMLButton>
          )}
        </div>
      </div>
      <ConnectionDisconnectModal
        isOpen={disconnectDialog.isOpen}
        serviceName={name}
        isBusy={disconnectDialog.isRunning}
        onCancel={disconnectDialog.cancel}
        onConfirm={disconnectDialog.confirm}
      />
    </div>
  );
};

export const SessionCaptureConnectionCard = TypedMemo(SessionCaptureConnectionCardComponent);
